import { useEffect, useState } from 'react';

/*
 * Single source of truth for JS-side breakpoints.
 * Keep these in sync with the CSS media queries:
 *   mobile  -> @media (max-width: 768px)
 *   tablet  -> @media (max-width: 1024px)
 *   heroStack -> @media (max-width: 1149px)  (home hero / feature planet)
 */
export const MQ = {
  mobile: '(max-width: 768px)',
  tablet: '(max-width: 1024px)',
  desktop: '(min-width: 1025px)',
  heroStack: '(max-width: 1149px)',
};

const getMatch = (query) =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;

/**
 * Reactive media query hook. Unlike reading window.innerWidth once at
 * module load, this updates on rotation and resize.
 */
export default function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => getMatch(query));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    // Safari < 14 only supports addListener
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);

  return matches;
}

export const useIsMobile = () => useMediaQuery(MQ.mobile);
export const useIsDesktop = () => useMediaQuery(MQ.desktop);
